<div class="row mb-3">
  <div class="col-12">
    <h2>Chain Branding</h2>
    <p class="text-muted">Customize your chain's visual identity</p>
  </div>
</div>

<div *ngIf="brandingForm" [formGroup]="brandingForm" class="row">

  <!-- Form Column -->
  <div class="col-md-7">
    <nb-card>
      <nb-card-header>
        <nb-icon icon="color-palette-outline"></nb-icon>
        Branding Settings
      </nb-card-header>
      <nb-card-body>

        <!-- Colors Section -->
        <h6 class="section-title">Colors</h6>

        <div class="row">
          <div class="col-md-6 mb-3">
            <label class="form-label">Primary Color</label>
            <div class="color-input-group">
              <input type="color" formControlName="primaryColor" class="color-swatch-input">
              <input type="text" nbInput fullWidth formControlName="primaryColor" placeholder="#3366FF" class="color-text-input">
            </div>
          </div>
          <div class="col-md-6 mb-3">
            <label class="form-label">Secondary Color</label>
            <div class="color-input-group">
              <input type="color" formControlName="secondaryColor" class="color-swatch-input">
              <input type="text" nbInput fullWidth formControlName="secondaryColor" placeholder="#FF6B35" class="color-text-input">
            </div>
          </div>
          <div class="col-md-6 mb-3">
            <label class="form-label">Accent Color</label>
            <div class="color-input-group">
              <input type="color" formControlName="accentColor" class="color-swatch-input">
              <input type="text" nbInput fullWidth formControlName="accentColor" placeholder="#00D68F" class="color-text-input">
            </div>
          </div>
          <div class="col-md-6 mb-3">
            <label class="form-label">Background Color</label>
            <div class="color-input-group">
              <input type="color" formControlName="backgroundColor" class="color-swatch-input">
              <input type="text" nbInput fullWidth formControlName="backgroundColor" placeholder="#F7F9FC" class="color-text-input">
            </div>
          </div>
        </div>

        <hr>

        <!-- Typography Section -->
        <h6 class="section-title">Typography</h6>

        <div class="mb-3">
          <label class="form-label">Font Family</label>
          <select nbSelect fullWidth formControlName="fontFamily" class="font-select">
            <option *ngFor="let font of fontOptions" [value]="font" [style.font-family]="font">
              {{ font }}
            </option>
          </select>
        </div>

        <hr>

        <!-- Images Section -->
        <h6 class="section-title">Images</h6>

        <!-- Logo -->
        <div class="mb-3">
          <label class="form-label fw-semibold">Logo</label>
          <div class="image-preview mb-2" *ngIf="logoPreview || brandingForm.get('logoUrl')?.value">
            <img [src]="logoPreview || chainsService.getFullImageUrl(brandingForm.get('logoUrl')?.value)"
                 alt="Logo preview"
                 style="max-height: 120px; max-width: 300px; object-fit: contain; border: 1px solid #ddd; border-radius: 4px; padding: 4px;">
          </div>
          <div class="d-flex align-items-center gap-2">
            <input #logoInput type="file" accept="image/*" (change)="onFileSelect($event, 'logo')" style="display:none">
            <button nbButton status="basic" size="small" type="button" (click)="logoInput.click()">
              <nb-icon icon="upload-outline"></nb-icon> Choose Logo
            </button>
            <span *ngIf="logoFile" class="text-muted small">{{ logoFile.name }}</span>
            <span *ngIf="!logoFile && brandingForm.get('logoUrl')?.value" class="text-muted small">Current file retained</span>
          </div>
          <!-- hidden field keeps the form value in sync -->
          <input type="text" formControlName="logoUrl" style="display:none">
        </div>

        <!-- Banner Image -->
        <div class="mb-3">
          <label class="form-label fw-semibold">Banner Image</label>
          <div class="image-preview mb-2" *ngIf="bannerPreview || brandingForm.get('bannerImageUrl')?.value">
            <img [src]="bannerPreview || chainsService.getFullImageUrl(brandingForm.get('bannerImageUrl')?.value)"
                 alt="Banner preview"
                 style="max-height: 120px; max-width: 300px; object-fit: contain; border: 1px solid #ddd; border-radius: 4px; padding: 4px;">
          </div>
          <div class="d-flex align-items-center gap-2">
            <input #bannerInput type="file" accept="image/*" (change)="onFileSelect($event, 'banner')" style="display:none">
            <button nbButton status="basic" size="small" type="button" (click)="bannerInput.click()">
              <nb-icon icon="upload-outline"></nb-icon> Choose Banner
            </button>
            <span *ngIf="bannerFile" class="text-muted small">{{ bannerFile.name }}</span>
            <span *ngIf="!bannerFile && brandingForm.get('bannerImageUrl')?.value" class="text-muted small">Current file retained</span>
          </div>
          <input type="text" formControlName="bannerImageUrl" style="display:none">
        </div>

        <!-- Favicon -->
        <div class="mb-3">
          <label class="form-label fw-semibold">Favicon / App Icon</label>
          <div class="image-preview mb-2" *ngIf="faviconPreview || brandingForm.get('faviconUrl')?.value">
            <img [src]="faviconPreview || chainsService.getFullImageUrl(brandingForm.get('faviconUrl')?.value)"
                 alt="Favicon preview"
                 style="max-height: 64px; max-width: 64px; object-fit: contain; border: 1px solid #ddd; border-radius: 4px; padding: 4px;">
          </div>
          <div class="d-flex align-items-center gap-2">
            <input #faviconInput type="file" accept="image/*,.ico" (change)="onFileSelect($event, 'favicon')" style="display:none">
            <button nbButton status="basic" size="small" type="button" (click)="faviconInput.click()">
              <nb-icon icon="upload-outline"></nb-icon> Choose Favicon
            </button>
            <span *ngIf="faviconFile" class="text-muted small">{{ faviconFile.name }}</span>
            <span *ngIf="!faviconFile && brandingForm.get('faviconUrl')?.value" class="text-muted small">Current file retained</span>
          </div>
          <input type="text" formControlName="faviconUrl" style="display:none">
        </div>

        <div class="mt-4">
          <button nbButton status="primary" size="medium" (click)="onSave()">
            <nb-icon icon="save-outline"></nb-icon>
            Save Branding
          </button>
        </div>

      </nb-card-body>
    </nb-card>
  </div>

  <!-- Preview Column -->
  <div class="col-md-5">
    <nb-card class="preview-card">
      <nb-card-header>
        <nb-icon icon="eye-outline"></nb-icon>
        Live Preview
      </nb-card-header>
      <nb-card-body>

        <!-- Preview Mockup -->
        <div class="preview-mockup" [style.background-color]="backgroundColor" [style.font-family]="fontFamily">

          <!-- Preview Header -->
          <div class="preview-header" [style.background-color]="primaryColor">
            <div class="preview-logo-area">
              <img *ngIf="logoUrl || logoPreview" [src]="logoPreview || chainsService.getFullImageUrl(logoUrl)" alt="Logo" class="preview-logo"
                   onerror="this.style.display='none'">
              <span *ngIf="!logoUrl && !logoPreview" class="preview-logo-placeholder" [style.color]="backgroundColor">
                LOGO
              </span>
            </div>
            <div class="preview-nav-dots">
              <span class="preview-nav-item" [style.color]="backgroundColor">Home</span>
              <span class="preview-nav-item" [style.color]="backgroundColor">Menu</span>
            </div>
          </div>

          <!-- Preview Body -->
          <div class="preview-body">
            <div class="preview-hero" [style.background-color]="secondaryColor + '33'">
              <h5 [style.color]="primaryColor" [style.font-family]="fontFamily">Welcome to your store</h5>
              <p class="preview-subtitle" [style.font-family]="fontFamily">Discover our products</p>
              <div class="preview-cta-btn" [style.background-color]="primaryColor" [style.color]="backgroundColor">
                Shop Now
              </div>
            </div>

            <!-- Color Swatches -->
            <div class="swatches-section">
              <p class="swatches-label">Color Palette</p>
              <div class="swatches-row">
                <div class="swatch-item">
                  <div class="swatch" [style.background-color]="primaryColor"></div>
                  <span class="swatch-name">Primary</span>
                </div>
                <div class="swatch-item">
                  <div class="swatch" [style.background-color]="secondaryColor"></div>
                  <span class="swatch-name">Secondary</span>
                </div>
                <div class="swatch-item">
                  <div class="swatch" [style.background-color]="accentColor"></div>
                  <span class="swatch-name">Accent</span>
                </div>
                <div class="swatch-item">
                  <div class="swatch swatch-bg" [style.background-color]="backgroundColor"></div>
                  <span class="swatch-name">Background</span>
                </div>
              </div>
            </div>

            <!-- Font Preview -->
            <div class="font-preview-section">
              <p class="swatches-label">Font: {{ fontFamily }}</p>
              <p class="font-preview-heading" [style.font-family]="fontFamily" [style.color]="primaryColor">
                The quick brown fox
              </p>
              <p class="font-preview-body" [style.font-family]="fontFamily">
                Jumps over the lazy dog
              </p>
            </div>

            <!-- Accent button -->
            <div class="preview-accent-area">
              <div class="preview-badge" [style.background-color]="accentColor">
                <nb-icon icon="checkmark-outline" style="color: white; font-size: 12px;"></nb-icon>
                Active
              </div>
            </div>

          </div>
        </div>

      </nb-card-body>
    </nb-card>
  </div>

</div>

<ngx-spinner bdColor="rgba(51,51,51,0.8)" size="large" [fullScreen]="true" color="white" type="ball-circus">
  <p style="color: white">Saving...</p>
</ngx-spinner>
